import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const UNITS_PER_METER = 2;
// Pepita vende en pasos de 0.5m, pero la compra mínima siempre es 1 metro
// entero — igual que ya impone el selector de la ficha de producto
// (snippets/quantity-selector.liquid). Shopify no tiene forma nativa de
// fijar esto a nivel de variante en plan Básico (QuantityRule es una
// función de catálogos B2B/Plus, no de tiendas DTC normales), así que el
// piso se impone aquí, en el tema, igual que en la ficha de producto.
const MIN_METERS = 1;

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * La tela se vende por metro, pero la unidad interna de Shopify es 0.5m
 * (1 unidad = 0.5m, ver SHOPIFY_VECINA_VENTA_MEDIOS_METROS.md). Este componente
 * muestra y opera siempre en METROS reales (no en la unidad interna cruda),
 * igual que ya hace el selector de la ficha de producto. La unidad interna
 * (metros x 2) solo se usa al despachar el evento que actualiza el carrito.
 *
 * OJO: tras cada actualización del carrito, Horizon resincroniza este input
 * con el HTML que devuelve el servidor (que sigue en unidad interna cruda),
 * usando un "morphing" que parchea atributos sobre el MISMO nodo ya
 * conectado — no vuelve a disparar connectedCallback. Por eso no basta con
 * convertir una sola vez al conectar: hay que detectar y reconvertir en cada
 * updateButtonStates() (que sí se invoca tras cada sincronización), usando
 * #lastKnownMeters como referencia de "lo último que escribimos nosotros".
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {number|null} */
  #lastKnownMeters = null;

  connectedCallback() {
    // super.connectedCallback() primero: inicializa this.refs (clase base
    // Component) — antes de esa llamada, this.refs.quantityInput no existe.
    super.connectedCallback();
    this.updateButtonStates();
  }

  /**
   * @param {number} n
   * @returns {string}
   */
  #formatMeters(n) {
    return (Math.round(n * UNITS_PER_METER) / UNITS_PER_METER).toString();
  }

  /**
   * Escribe un valor en metros en el input (value + defaultValue, para que
   * un rollback por error de carrito también restaure metros) y recuerda
   * que fuimos nosotros quienes lo escribimos.
   * @param {number} meters
   */
  #writeMeters(meters) {
    const { quantityInput } = this.refs;
    const formatted = this.#formatMeters(meters);
    quantityInput.value = formatted;
    quantityInput.defaultValue = formatted;
    this.#lastKnownMeters = parseFloat(formatted);
  }

  /**
   * Convierte min/max/step de unidad interna cruda a metros. Es idempotente
   * (usa un flag en dataset) porque, a diferencia de value/data-cart-quantity,
   * estos casi nunca cambian entre sincronizaciones del carrito.
   */
  #reconcileConstraints() {
    const { quantityInput } = this.refs;
    if (quantityInput.dataset.constraintsInMeters === 'true') return;

    const rawMin = parseInt(quantityInput.min, 10) || UNITS_PER_METER;
    const rawStep = parseInt(quantityInput.step, 10) || 1;
    const rawMax = quantityInput.max ? parseInt(quantityInput.max, 10) : null;

    // Shopify no fija ningún mínimo real en esta tienda (quantity_rule.min
    // por defecto = 1 unidad interna = 0.5m) — sin este suelo, "-" dejaría
    // bajar por debajo del metro mínimo de compra.
    quantityInput.min = this.#formatMeters(Math.max(rawMin / UNITS_PER_METER, MIN_METERS));
    quantityInput.step = this.#formatMeters(rawStep / UNITS_PER_METER);
    if (rawMax !== null) {
      quantityInput.max = this.#formatMeters(rawMax / UNITS_PER_METER);
    }
    quantityInput.dataset.constraintsInMeters = 'true';
  }

  /**
   * Detecta si el valor actual del input coincide con lo último que
   * ESTE componente escribió (#lastKnownMeters). Si no coincide, asume
   * que el servidor acaba de resincronizar el DOM en unidad interna cruda
   * (vía morphing, sin pasar por nuestros métodos) y lo reconvierte a
   * metros. Se llama en cada updateButtonStates().
   */
  #reconcileFromServer() {
    const { quantityInput } = this.refs;
    this.#reconcileConstraints();

    const current = parseFloat(quantityInput.value);
    if (Number.isNaN(current)) return;

    if (this.#lastKnownMeters !== null && Math.abs(current - this.#lastKnownMeters) < 1e-9) {
      return; // ya está en metros y coincide con lo último que escribimos
    }

    // "current" está en unidad interna cruda (recién sincronizado por el
    // servidor) — convertir a metros.
    this.#writeMeters(current / UNITS_PER_METER);

    const rawCartQuantity = parseFloat(quantityInput.getAttribute('data-cart-quantity'));
    if (!Number.isNaN(rawCartQuantity)) {
      quantityInput.setAttribute('data-cart-quantity', this.#formatMeters(rawCartQuantity / UNITS_PER_METER));
    }
  }

  /**
   * Lee los valores actuales en metros (floats), no en unidad interna (int).
   * Sobrescribe por completo el parseo entero de la clase base: una vez que
   * el input pasa a contener metros (posiblemente con decimales, ej. "2.5"),
   * el `parseInt` de la clase base los truncaría mal (leería "2").
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;
    const parse = (/** @type {string | null} */ v, /** @type {number|null} */ def) => {
      if (v === null || v === '') return def;
      const n = parseFloat(v);
      return Number.isNaN(n) ? def : n;
    };

    return {
      min: /** @type {number} */ (parse(quantityInput.min, 1)),
      max: quantityInput.max ? parse(quantityInput.max, null) : null,
      step: /** @type {number} */ (parse(quantityInput.step, 0.5)),
      value: /** @type {number} */ (parse(quantityInput.value, 0)),
      cartQuantity: /** @type {number} */ (parse(quantityInput.getAttribute('data-cart-quantity'), 0)),
    };
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled.
   * Este es también el punto donde reconciliamos el valor tras cada
   * sincronización con el servidor (ver #reconcileFromServer).
   */
  updateButtonStates() {
    this.#reconcileFromServer();

    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Updates cart quantity and refreshes component state.
   * @param {number} cartQty - La cantidad en el carrito, en unidad interna
   *   cruda (así es como la entrega component-cart-items.js).
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', this.#formatMeters(cartQty / UNITS_PER_METER));
    this.updateCartQuantity();
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment.
   * @param {string} min - Minimum value, en unidad interna cruda.
   * @param {string|null} max - Maximum value, en unidad interna cruda.
   * @param {string} step - Step increment, en unidad interna cruda.
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    const metersMin = this.#formatMeters((parseInt(min, 10) || UNITS_PER_METER) / UNITS_PER_METER);
    const metersStep = this.#formatMeters((parseInt(step, 10) || 1) / UNITS_PER_METER);

    quantityInput.min = metersMin;
    quantityInput.step = metersStep;
    if (max) {
      quantityInput.max = this.#formatMeters(parseInt(max, 10) / UNITS_PER_METER);
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.dataset.constraintsInMeters = 'true';

    const { value } = this.getCurrentValues();
    const newMin = parseFloat(metersMin);
    const newStep = parseFloat(metersStep) || 0.5;
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned (tolerancia de coma
    // flotante en vez de "% !== 0", porque aquí trabajamos con decimales).
    let newValue = value;
    const stepsFromMin = (value - newMin) / newStep;
    if (Math.abs(Math.round(stepsFromMin) - stepsFromMin) > 1e-9) {
      newValue = newMin + Math.floor(stepsFromMin) * newStep;
    }

    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== value) {
      this.#writeMeters(newValue);
    }

    this.updateButtonStates();
  }

  /**
   * Updates quantity by a given step (en metros).
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    this.#writeMeters(newValue);
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity set event (on blur). Validates and snaps to valid
   * values, en metros.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const requested = parseFloat(event.target.value.replace(',', '.'));
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, Number.isNaN(requested) ? 0 : requested));

    const stepsFromMin = (quantity - min) / step;
    if (Math.abs(Math.round(stepsFromMin) - stepsFromMin) > 1e-9) {
      // Snap DOWN al múltiplo válido más cercano en vez de disparar la
      // validación nativa (que en un input de tipo "number" con decimales
      // es menos fiable que el snapping entero de la clase base).
      const snapped = min + Math.floor(stepsFromMin) * step;
      this.#writeMeters(Math.max(min, snapped));
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    this.#writeMeters(quantity);
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event. El input visible está en metros;
   * lo que de verdad se manda al carrito (y a Shopify) sigue siendo la
   * unidad interna cruda (metros x 2) — este es el único punto de
   * conversión de vuelta.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const metersValue = parseFloat(quantityInput.value) || 0;
    const rawValue = Math.round(metersValue * UNITS_PER_METER);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(rawValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * El botón "Checkout" hace un submit NATIVO del formulario del carrito
   * (name="checkout", sin pasar por onQuantityChange/fetch como el resto
   * de interacciones de este componente) — el navegador serializa el
   * input tal cual está, que siempre muestra METROS reales, no la unidad
   * interna cruda que Shopify espera en "updates[]". Sin esto, un submit
   * nativo mandaría la mitad de la cantidad real pedida por cada tela.
   * Debe llamarse justo antes de que el formulario se envíe (ver
   * component-cart-items.js, listener de "submit" en #cart-form).
   */
  prepareForNativeSubmit() {
    const { quantityInput } = this.refs;
    const metersValue = parseFloat(quantityInput.value);
    if (Number.isNaN(metersValue)) return;

    quantityInput.value = Math.round(metersValue * UNITS_PER_METER).toString();
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      this.#writeMeters(clampedValue);
    }

    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
